import { ExamType } from "../../../model/Exam";
import { ExamAndExpertAssignmentRequestModel, ExamAssignment } from "../../../model/ExamAssignmentRequestModel";
import { ExamDayType } from "../../../model/ExamDayType";
import { ExamDayViewModel } from "../../../model/ExamDayViewModel";
import { CandidateAssignment } from "../../../model/examSessionCandidate/AssignCandidatesRequestModel";
import { ExamSessionCandidateExamState } from "../../../model/examSessionCandidate/ExamSessionCandidateExamViewModel";
import { ExamSessionCandidateViewModel } from "../../../model/examSessionCandidate/ExamSessionCandidateViewModel";
import { ExamSessionViewModel } from "../../../model/ExamSessionViewModel";
import { TimeSlotStructure } from "../../../model/TimeSlotStructure";
import { getExamTimeSlots } from "../../../utils/TimeSlotUtils";
import { SchedulerCandidate, SchedulerExamCandidate } from "./SchedulerCandidateContext";
import { ExamCandidateCaseName } from "./SchedulerCaseNameContext";
import { SchedulerRoomExperts } from "./SchedulerExpertContext";

export const getLocation = (examSession: ExamSessionViewModel | undefined, locationId: string) =>
  examSession?.locations.find((location) => location.id === locationId);

export const getExamDay = (allExamDays: ExamDayViewModel[], selectedExamDayId: string) =>
  allExamDays.find((day) => day.id === selectedExamDayId);

const getRoomAssignmentFromExamDay = (examDay: ExamDayViewModel, roomId: string) =>
  examDay.roomAssignments.find((assignment) => assignment.roomId === roomId);

const getRoomAssignment = (allExamDays: ExamDayViewModel[], selectedExamDayId: string, roomId: string) => {
  const examDay = getExamDay(allExamDays, selectedExamDayId);
  if (examDay === undefined) {
    return undefined;
  }

  return getRoomAssignmentFromExamDay(examDay, roomId);
};

const getRoomExamAssignments = (allExamDays: ExamDayViewModel[], selectedExamDayId: string, roomId: string) => {
  const roomAssignment = getRoomAssignment(allExamDays, selectedExamDayId, roomId);
  return roomAssignment?.examAssignments;
};

export const getCandidatesContextData = (
  examSessionCandidates: ExamSessionCandidateViewModel[],
  examDays: ExamDayViewModel[]
) => {
  const schedulerCandidates: SchedulerCandidate[] = [];
  const schedulerExamCandidates: SchedulerExamCandidate[] = [];
  const examCandidateCaseNames: ExamCandidateCaseName[] = [];

  const examDayIds = examDays.map((d) => d.id);

  examSessionCandidates.forEach((examSessionCandidate) => {
    // Do not show cancelled exams or exams for other exam days
    const exams = examSessionCandidate.exams.filter(
      (exam) => exam.state !== ExamSessionCandidateExamState.Cancelled && examDayIds.includes(exam.examDayId)
    );

    if (!exams.length) {
      return;
    }

    schedulerCandidates.push({
      candidateId: examSessionCandidate.candidateId,
      firstName: examSessionCandidate.firstName,
      lastName: examSessionCandidate.lastName,
      language: examSessionCandidate.language,
    });

    exams.forEach((exam) => {
      const module = examSessionCandidate.modules.find((module) => module.moduleId === exam.moduleId);
      schedulerExamCandidates.push({
        candidateId: examSessionCandidate.candidateId,
        moduleOption: module!.option,
        examId: exam.examId,
        examState: exam.state,
        examDayId: exam.examDayId,
        roomId: exam.roomId,
        timeSlotIndex: exam.timeSlotIndex,
      });
      examCandidateCaseNames.push({
        candidateId: examSessionCandidate.candidateId,
        examId: exam.examId,
        caseName: exam.caseName,
      });
    });
  });

  return {
    schedulerCandidates,
    schedulerExamCandidates,
    examCandidateCaseNames,
  };
};

export const getExamTypes = (examDay?: ExamDayViewModel) => {
  switch (examDay?.type) {
    case ExamDayType.Correction:
      return [ExamType.TermPaper, ExamType.Written, ExamType.Oral];
    case ExamDayType.Written:
      return [ExamType.Written];
    case ExamDayType.Oral:
      return [ExamType.Oral];
    default:
      return [ExamType.Written];
  }
};

export const isOralDay = (examTypes: ExamType[]) => examTypes.length === 1 && examTypes[0] === ExamType.Oral;

export const addTimeSlotExam = (
  allExamDays: ExamDayViewModel[],
  selectedExamDayId: string,
  roomId: string,
  examId: string,
  timeSlotIndex: number
) => {
  const examAssignments = getRoomExamAssignments(allExamDays, selectedExamDayId, roomId);
  if (examAssignments === undefined) {
    return;
  }

  examAssignments.push({
    examId,
    timeSlotIndex,
  });
};

export const removeTimeSlotExam = (
  allExamDays: ExamDayViewModel[],
  selectedExamDayId: string,
  roomId: string,
  examId: string,
  timeSlotIndex: number
) => {
  const examAssignments = getRoomExamAssignments(allExamDays, selectedExamDayId, roomId);
  if (examAssignments === undefined) {
    return;
  }

  const examAssignment = examAssignments.find(
    (assignment) => assignment.examId === examId && assignment.timeSlotIndex === timeSlotIndex
  );
  if (examAssignment === undefined) {
    return;
  }

  const index = examAssignments.indexOf(examAssignment);
  examAssignments.splice(index, 1);
};

export const clearTimeSlotExams = (
  allExamDays: ExamDayViewModel[],
  selectedExamDayId: string,
  roomId: string,
  timeSlotIndex: number
) => {
  const roomAssignment = getRoomAssignment(allExamDays, selectedExamDayId, roomId);
  if (roomAssignment === undefined) {
    return;
  }

  roomAssignment.examAssignments = roomAssignment.examAssignments.filter(
    (assignment) => assignment.timeSlotIndex !== timeSlotIndex
  );
};

export const setAllTimeSlotsExams = (
  allExamDays: ExamDayViewModel[],
  selectedExamDayId: string,
  timeSlotStructure: TimeSlotStructure,
  roomId: string,
  examIds: string[]
) => {
  const examDay = getExamDay(allExamDays, selectedExamDayId);
  if (examDay === undefined) {
    return;
  }

  const roomAssignment = getRoomAssignmentFromExamDay(examDay, roomId);
  if (roomAssignment === undefined) {
    return;
  }

  const timeSlotIndexes = getExamTimeSlots(timeSlotStructure, examDay?.timeSlotStructureType).map(
    (timeSlot) => timeSlot.index as number
  );
  roomAssignment.examAssignments = [];
  timeSlotIndexes.forEach((timeSlotIndex) =>
    examIds.forEach((examId) =>
      roomAssignment.examAssignments.push({
        examId,
        timeSlotIndex,
      })
    )
  );
};

export const areRoomsNotOverloaded = (
  examSession: ExamSessionViewModel | undefined,
  locationId: string,
  schedulerExamCandidates: SchedulerExamCandidate[]
) => {
  const location = getLocation(examSession, locationId);
  if (location === undefined) {
    return false;
  }

  // Get all unique slots being used.
  const uniqueSlots = new Array<{
    examDayId: string;
    roomId: string;
    timeSlotIndex: number;
    candidatesCount: number;
  }>();
  const locationRoomIds = location.rooms.map((room) => room.id);
  const bookedExamCandidatesInLocationRooms = schedulerExamCandidates.filter(
    (schedulerExamCandidate) =>
      schedulerExamCandidate.examState !== ExamSessionCandidateExamState.Cancelled &&
      locationRoomIds.includes(schedulerExamCandidate.roomId)
  );
  bookedExamCandidatesInLocationRooms.forEach((schedulerExamCandidate) => {
    const { examDayId, roomId, timeSlotIndex } = schedulerExamCandidate;
    const existingSlot = uniqueSlots.find(
      (slot) => slot.examDayId === examDayId && slot.roomId === roomId && slot.timeSlotIndex === timeSlotIndex
    );
    if (existingSlot !== undefined) {
      existingSlot.candidatesCount++;
    } else {
      uniqueSlots.push({
        examDayId,
        roomId,
        timeSlotIndex,
        candidatesCount: 1,
      });
    }
  });

  // For each of those slots, check if the room, for that day and time slot, is not overloaded.
  const roomsNotOverloaded = uniqueSlots.every((slot) => {
    const room = location.rooms.find((room) => room.id === slot.roomId);
    return room && room.numberOfSeats >= slot.candidatesCount;
  });
  return roomsNotOverloaded;
};

export const getAssignCandidatesAndExpertsRequestModel = (
  schedulerExamCandidates: SchedulerExamCandidate[],
  examCandidateCaseNames: ExamCandidateCaseName[],
  examDays: ExamDayViewModel[],
  schedulerExperts: SchedulerRoomExperts[],
  examSessionId: string,
  examSessionCandidates?: ExamSessionCandidateViewModel[]
) => {
  const candidateAssignments: CandidateAssignment[] = [];
  const examDayIds = examDays.map((d) => d.id);
  schedulerExamCandidates
    .filter((schedulerExamCandidate) => examDayIds.includes(schedulerExamCandidate.examDayId))
    .forEach((schedulerExamCandidate) => {
      const examSessionCandidate = examSessionCandidates?.find(
        (examSessionCandidate) => examSessionCandidate.candidateId === schedulerExamCandidate.candidateId
      );
      let candidateAssignment = candidateAssignments.find(
        (candidateAssignment) => candidateAssignment.examSessionCandidateId === examSessionCandidate!.id
      );
      if (candidateAssignment === undefined) {
        candidateAssignment = {
          examSessionCandidateId: examSessionCandidate!.id,
          exams: [],
        };
        candidateAssignments.push(candidateAssignment);
      }

      const examCandidateCaseName = examCandidateCaseNames.find(
        (examCandidate) =>
          examCandidate.candidateId === schedulerExamCandidate.candidateId &&
          examCandidate.examId === schedulerExamCandidate.examId
      );
      candidateAssignment.exams.push({
        examId: schedulerExamCandidate.examId,
        examDayId: schedulerExamCandidate.examDayId,
        roomId: schedulerExamCandidate.roomId,
        timeSlotIndex: schedulerExamCandidate.timeSlotIndex,
        caseName: examCandidateCaseName?.caseName,
      });
    });
  return { examSessionCandidates: candidateAssignments, examSessionExperts: schedulerExperts, examSessionId };
};

export const getExamAndExpertsAssignmentRequestModels = (
  examDays: ExamDayViewModel[],
  schedulerExperts: SchedulerRoomExperts[]
) => {
  const examAssignments: ExamAssignment[] = [];
  examDays.forEach((examDay) => {
    examDay.roomAssignments.forEach((roomAssignment) => {
      if (!roomAssignment.isPreparationRoom) {
        examAssignments.push({
          examDayId: examDay.id,
          roomExamDayAssignmentId: roomAssignment.id,
          examAssignments: roomAssignment.examAssignments,
        });
      }
    });
  });
  const examAndExpertAssignModel: ExamAndExpertAssignmentRequestModel = {
    examSessionExams: examAssignments,
    examSessionExperts: schedulerExperts,
  };
  return examAndExpertAssignModel;
};

export const getExamDays = (
  examSession: ExamSessionViewModel | undefined,
  locationId: string,
  examDayType: ExamDayType
) =>
  examSession?.examDays
    .filter((examDay) => examDay.locationId === locationId && examDay.type === examDayType)
    .sort((day1, day2) => new Date(day1.date).getTime() - new Date(day2.date).getTime());
